import React, { useState } from 'react';
import {
  IonCard,
  IonCardContent,
  IonCardHeader,
  IonCardTitle,
  IonInput,
  IonTextarea,
  IonButton,
  IonIcon,
  IonText,
  IonItem,
  IonLabel,
  IonCheckbox,
  // IonChip,
} from '@ionic/react';
import {
  documentAttach,
  attachOutline,
  chatbubbleEllipses,
  chatbubbleEllipsesOutline 
} from 'ionicons/icons';
import { ChecklistItemFormData, FileAttachment, AttachmentType } from '@/types/model/inspection-modal';
import { ChecklistItem } from '@/types/model/inspection';
import AttachmentsSection from '@/components/common/AttachmentsSection';
import { FileResponse } from '@/types/response/InspectionResultResponses';
import { Capacitor } from '@capacitor/core';

interface InspectionChecklistProps {
  checklistItems: ChecklistItem[];
  globalFiles: FileResponse[];
  formData: ChecklistItemFormData[];
  onItemChange: (itemId: number, data: Partial<ChecklistItemFormData>) => void;
  readOnly?: boolean;
}

const InspectionChecklist: React.FC<InspectionChecklistProps> = ({
  checklistItems,
  globalFiles,
  formData,
  onItemChange,
  readOnly = false,
}) => {
  const [expandedSections, setExpandedSections] = useState<{[key: string]: boolean}>({});
  const isMobilePlatform = Capacitor.getPlatform() !== 'web';

  const toggleSection = (itemId: number, section: 'comments' | 'attachments') => {
    const key = `${itemId}-${section}`;
    setExpandedSections(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const isSectionExpanded = (itemId: number, section: 'comments' | 'attachments') => {
    const key = `${itemId}-${section}`;
    return expandedSections[key] || false;
  };

  // Sort checklist items by Order field
  const sortedItems = [...checklistItems].sort((a, b) => a.Order - b.Order);

  const getFormData = (itemId: number): ChecklistItemFormData => {
    return formData.find(item => item.id === itemId) || {
      id: itemId,
      score: null,
      comment: '',
      attachments: [],
    };
  };

  const handleScoreChange = (itemId: number, value: string) => {
    if (value === '') {
      onItemChange(itemId, { score: null });
      return;
    }
    
    const numericValue = parseFloat(value);
    // Allow up to 1 decimal place and validate range
    if (!isNaN(numericValue) && numericValue >= 0 && numericValue <= 10) {
      // Round to 1 decimal place to prevent floating point precision issues
      const roundedValue = Math.round(numericValue * 10) / 10;
      onItemChange(itemId, { score: roundedValue });
    }
  };

  const handleNotApplicableChange = (itemId: number, isChecked: boolean) => {
    if (isChecked) {
      onItemChange(itemId, { score: null });
    } else {
      onItemChange(itemId, { score: 0 });
    }
  };

  const isNotApplicable = (score: number | null) => {
    return score === null || score === undefined;
  };

  const handleCommentChange = (itemId: number, value: string) => {
    onItemChange(itemId, { comment: value });
  };
  
  const removeAttachment = (itemId: number, index: number) => {
    const currentData = getFormData(itemId);
    const updatedAttachments = currentData.attachments.filter((_, i) => i !== index);
    onItemChange(itemId, { attachments: updatedAttachments });
  };

  const handleAttachmentDescriptionChange = (itemId: number, index: number, description: string) => {
    const currentData = getFormData(itemId);
    const updatedAttachments = currentData.attachments.map((attachment, i) =>
      i === index ? { ...attachment, description } : attachment
    );
    onItemChange(itemId, { attachments: updatedAttachments });
  };

  const handleAttachmentFileChange = (itemId: number, index: number, file: File) => {
    const currentData = getFormData(itemId);
    const updatedAttachments = currentData.attachments.map((attachment, i) =>
      i === index ? { ...attachment, file, fileName: file.name, size: file.size } : attachment
    );
    onItemChange(itemId, { attachments: updatedAttachments });
  };

  const handleAttachmentLinkChange = (itemId: number, index: number, link: string) => {
    const currentData = getFormData(itemId);
    const updatedAttachments = currentData.attachments.map((attachment, i) =>
      i === index ? { ...attachment, link } : attachment
    );
    onItemChange(itemId, { attachments: updatedAttachments });
  };

  const handleExistingFileSelect = (itemId: number, index: number, fileId: string) => {
    const currentData = getFormData(itemId);
    const updatedAttachments = currentData.attachments.map((attachment, i) =>
      i === index ? { ...attachment, fileId } : attachment
    );
    onItemChange(itemId, { attachments: updatedAttachments });
  };

  const handleAaddAttachment = (itemId: number, type: AttachmentType) => {
    if (type === 'image' && !isMobilePlatform) {
      return;
    }

    const currentData = getFormData(itemId);
    const newAttachment: FileAttachment = { type: type };
    const updatedAttachments = [...currentData.attachments, newAttachment];
    onItemChange(itemId, { attachments: updatedAttachments });
  }

  return (
    <div style={{ padding: '8px' }}>
      {sortedItems.map((item) => {
        const itemFormData = getFormData(item.Id);
        const commentsExpanded = isSectionExpanded(item.Id, 'comments');
        const attachmentsExpanded = isSectionExpanded(item.Id, 'attachments');
        
        return (
          <IonCard 
            key={item.Id}
            style={{ 
              marginBottom: '16px',
              width: '100%',
              maxWidth: 'none'
            }}
          >
            <IonCardHeader style={{ paddingBottom: '8px' }}>
              {/* Title and Score in same row */}
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                gap: '16px',
                flexWrap: 'wrap'
              }}>
                <div style={{ flex: '1', minWidth: '200px' }}>
                  <IonCardTitle style={{ 
                    fontSize: '1.1rem',
                    fontWeight: 'bold',
                    margin: '0'
                  }}>
                    <strong>{item.Order}</strong> {item.Text}
                  </IonCardTitle>
                </div>
                
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '12px',
                  minWidth: '200px'
                }}>
                  {/* Score Input */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                    <IonText style={{ fontSize: '0.9rem', whiteSpace: 'nowrap' }}>
                      Score:
                    </IonText>
                    <IonInput
                      style={{ 
                        maxWidth: '80px',
                        '--background': isNotApplicable(itemFormData.score) ? '#f0f0f0' : '#f8f9fa',
                        '--border-radius': '4px',
                        '--color': isNotApplicable(itemFormData.score) ? '#999' : 'inherit'
                      }}
                      type="number"
                      min="0"
                      max="10"
                      step="0.1"
                      disabled={readOnly || isNotApplicable(itemFormData.score)}
                      value={itemFormData.score?.toString() || ''}
                      onIonChange={(e) => handleScoreChange(item.Id, e.detail.value!)}
                      placeholder="0-10"
                    />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <IonCheckbox
                        checked={isNotApplicable(itemFormData.score)}
                        disabled={readOnly}
                        onIonChange={(e) => handleNotApplicableChange(item.Id, e.detail.checked)}
                      />
                      <IonText style={{ fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                        N/A
                      </IonText>
                    </div>
                  </div>
                  
                  <div style={{ display: 'flex', gap: '4px' }}>
                    <IonButton
                      fill="clear"
                      size="small"
                      disabled={readOnly}
                      onClick={() => toggleSection(item.Id, 'comments')}
                    >
                      <IonIcon icon={ item.Comment ? chatbubbleEllipses : chatbubbleEllipsesOutline } style={{ fontSize: '18px' }} />
                    </IonButton>
                    <IonButton
                      fill="clear" 
                      size="small"
                      disabled={readOnly}
                      onClick={() => toggleSection(item.Id, 'attachments')}
                    >
                      <IonIcon icon={ itemFormData.attachments.length > 0 ? documentAttach : attachOutline } style={{ fontSize: '18px' }} />
                    </IonButton>
                  </div>
                </div>
              </div>
            </IonCardHeader>
            
            <IonCardContent style={{ paddingTop: '8px' }}>
              {/* Scoring Criteria */}
              {item.ResultsAnalysis && (
                <div style={{ marginBottom: '16px' }}>
                  <IonText color="medium" style={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    Scoring Criteria:
                  </IonText>
                  <IonText style={{ display: 'block', marginTop: '4px', fontSize: '0.9rem' }}>
                    {item.ResultsAnalysis}
                  </IonText>
                </div>
              )}

              {/* Collapsible Comments Section */}
              {commentsExpanded && (
                <div style={{ marginBottom: '16px' }}>
                  <IonItem lines="none" style={{ '--background': 'transparent', '--padding-start': '0' }}>
                    <IonLabel position="stacked" style={{ marginBottom: '8px' }}>
                      <strong>Comments</strong>
                    </IonLabel>
                    <IonTextarea
                      value={itemFormData.comment || ''}
                      disabled={readOnly}
                      onIonChange={(e) => handleCommentChange(item.Id, e.detail.value!)}
                      placeholder="Enter comments (optional)"
                      rows={3}
                      autoGrow={true}
                      style={{ 
                        '--background': '#f8f9fa',
                        '--border-radius': '4px'
                      }}
                    />
                  </IonItem>
                </div>
              )}

              {/* Collapsible Attachments Section */}
              {attachmentsExpanded && (
                <AttachmentsSection
                  attachments={itemFormData.attachments}
                  onAddAttachment={(type) => handleAaddAttachment(item.Id, type)}
                  onDelete={(index) => removeAttachment(item.Id, index)}
                  onFileUpdate={(index, file) => handleAttachmentFileChange(item.Id, index, file)}
                  onLinkUpdate={(index, link) => handleAttachmentLinkChange(item.Id, index, link)}
                  onExistingFileSelect={(index, fileId) => handleExistingFileSelect(item.Id, index, fileId)}
                  onDescriptionUpdate={(index, description) => handleAttachmentDescriptionChange(item.Id, index, description)}
                  existingFiles={globalFiles}
                  readOnly={readOnly}
                  collapsible={true}
                  isExpanded={isSectionExpanded(item.Id, 'attachments')}
                  onExpandChange={(expanded) => {
                    if (expanded !== isSectionExpanded(item.Id, 'attachments')) {
                      toggleSection(item.Id, 'attachments');
                    }
                  }}
                  existingAttachmentCount={item.AttachmentCount}
                />
              )}
            </IonCardContent>
          </IonCard>
        );
      })}
    </div>
  );
};

export default InspectionChecklist;